import type { Attachment as FormattedAttachment } from "@/api/attachment";
import type { MultiplesampleMouvement } from "@/api/multiplesample-mouvement.api";
import type { TirageMouvement } from "@/api/tirages/tirage-mouvement.api";
import type { TirageHistory } from "@/api/tirages/tirage-history.api";
import { Tirage as TirageApi } from "@/api/tirages/tirage.api";
import { LocaleDict, multilingualFieldSchema, SupportedLocale } from "@/i18n";
import { z } from "zod";
import { DimensionsDTO, LegacyMoneyAmount, MoneyAmount } from "./shared-types";
import { Overwrite } from "./utils";

// Re-export Tirage with relations loaded for overview / drawer views
export interface Tirage extends TirageApi {
  attachments?: Attachment[];
  tirageHistory?: TirageHistory[];
  mouvements?: TirageMouvement[];
  tirageInAuction?: TirageInAuction[];
  tirageInExhibition?: TirageInExhibition[];
  tirageInBibliography?: TirageInBibliography[];
}

export type PublicationStatus = "published" | "draft" | "bin";

/** Simple = multiple classique ; Portfolio = contient des multiples simples (MultipleprintInPortfolio). */
export const MULTIPLEPRINT_NATURES = ["simple", "portfolio"] as const;
export type MultipleprintNature = (typeof MULTIPLEPRINT_NATURES)[number];

export interface ArtworkBasic {
  id: string;
  instanceId: string;
  oeuvresPublicationStatus: PublicationStatus;
  oeuvresDescriptionDimensionsOeuvre: string;
  oeuvresSocleDimensions: string;
  oeuvresDescriptionDimensionsOeuvreRaw: string;
  oeuvresDescriptionDimensionsHorsMarges: string;
  sources: string;
  oeuvresDescriptionNumeroEnregistrement: string;
  oeuvresDescriptionNumeroEnregistrementPublished: boolean;
  oeuvresDescriptionNomArtisteAttribueA: boolean;
  oeuvresDescriptionNomArtisteAnonyme: boolean;
  oeuvresDescriptionNomArtisteDansLeGoutDe: boolean;
  oeuvresDescriptionNomArtisteEntourageDe: boolean;
  oeuvresDescriptionNomArtisteDApres: boolean;
  oeuvresDescriptionNomArtisteAtelierDe: boolean;
  oeuvresDescriptionTitre: string;
  oeuvresDescriptionTitreSansTitre: boolean;
  oeuvresDescriptionTitreInconnu: boolean;
  oeuvresDescriptionTitreMultilingual: LocaleDict<string>;
  oeuvresDescriptionTitreMultilingualPublished: boolean;
  oeuvresDescriptionAncienTitre: string;
  oeuvresDescriptionAncienTitrePublished: boolean;
  oeuvresDescriptionDescriptionLongue: string;
  oeuvresDescriptionSousTitre: string;
  oeuvresDescriptionSousTitrePublished: boolean;
  oeuvresDescriptionAutreTitre: string;
  oeuvresDescriptionAutreTitrePublished: boolean;
  oeuvresDescriptionReference: string;
  oeuvresDescriptionReferencePublished: boolean;
  oeuvresDescriptionDateDeCreation: string;
  oeuvresDescriptionDateDeCreationInconnue: boolean;
  oeuvresDescriptionDateDeCreationIncertaine: boolean;
  oeuvresDescriptionDateDeCreationEstimee: boolean;
  oeuvresDescriptionPeriodeCreation: string;
  oeuvresDescriptionAireGeographique: string;
  oeuvresDescriptionEcoleMouvement: string;
  oeuvresDescriptionCategorie: string;
  oeuvresDescriptionSerie: string;
  oeuvresDescriptionTheme: string;
  oeuvresDescriptionLieuDeCreation: string;
  oeuvresDescriptionDimensionsOeuvreVerifiees: boolean;
  oeuvresdescriptiondimensionsprofondeur: boolean | null;
  oeuvresDescriptionDimensionsOeuvreInconnues: boolean;
  oeuvresDescriptionDimensionsOeuvreDouteuses: boolean;
  oeuvresDescriptionDimensionsHorsMargesVerifiees: boolean;
  oeuvresDescriptionDimensionsHorsMargesInconnues: boolean;
  oeuvresDescriptionDimensionsHorsMargesDouteuses: boolean;
  oeuvresDescriptionPoidsOeuvre: string;
  oeuvresDescriptionPoidsOeuvrePublished: boolean;
  oeuvresSocleDescription: LocaleDict<string>;
  oeuvresSocleDescriptionPublished: boolean;
  oeuvresSoclePoids: string;
  oeuvresSculptureDescriptionEditions: LocaleDict<string>;
  oeuvresSculptureDescriptionEditionsPublished: boolean;
  oeuvresSculpturePresentationSculpture: LocaleDict<string>;
  oeuvresSculpturePresentationSculpturePublished: boolean;
  oeuvresSculptureNotesGenerales: LocaleDict<string>;
  oeuvresSculptureNotesGeneralesPublished: boolean;
  oeuvresSculptureMouleDescription: LocaleDict<string>;
  oeuvresSculptureMouleNombreElements: string;
  oeuvresSculptureMouleDimensions: string;
  oeuvresSculptureMouleDimensionsPersonnalisees: string;
  oeuvresSculptureMoulePoids: string;
  oeuvresSculptureMouleLocalisation: string;
  oeuvresSculptureMouleNonLocalise: boolean;
  oeuvresSculptureMouleDestroy: boolean;
  oeuvresSculptureMouleFabricant: string;
  oeuvresSculptureMouleCoutRevient: MoneyAmount;
  oeuvresSculptureMouleNotes: string;
  oeuvresDescriptionOeuvreDoubleFace: boolean;
  oeuvresDescriptionTypeOeuvreMultilingual: LocaleDict<string>;
  oeuvresStatus: string;
  oeuvresDescriptionTechniqueFRA: string;
  oeuvresDescriptionTechniqueGB: string;

  oeuvresDescriptionTechnique: LocaleDict<string>;

  oeuvresDescriptionSupportFRA: string;
  oeuvresDescriptionSupportGB: string;

  oeuvresDescriptionSupport: LocaleDict<string>;

  oeuvresDescriptionRemarqueSupportFRA: string;
  oeuvresDescriptionRemarqueSupportGB: string;

  oeuvresDescriptionRealisationFabrication: string;
  oeuvresDescriptionSignatureTranscription: string;

  oeuvresDescriptionSignatureLocalisationFRA: string;
  oeuvresDescriptionSignatureLocalisationGB: string;

  oeuvresDescriptionSignatureLocalisation: LocaleDict<string>;

  oeuvresDescriptionInscriptionsDos: string;
  oeuvresDescriptionInscriptionsDosEN: string;

  oeuvresDescriptionInscriptionsDosMultilingual: LocaleDict<string>;
  oeuvresDescriptionInscriptionsDosPublished: LocaleDict<boolean>;

  oeuvresDescriptionResume: string;
  oeuvresDescriptionLegendeDocumentAnnexe: string;
  oeuvresDescriptionNomFichierNumerique: string;
  oeuvresDescriptionNumeroDInventaire: string;
  oeuvresDescriptionNumeroDInventairePublished: boolean;
  oeuvresDescriptionReferenceCollectionActuelle: string;
  oeuvresDescriptionNumeroAutre: string;
  oeuvresInformationAutreNumeroPublished: boolean;
  oeuvresInformationAncienNumero: string;
  oeuvresInformationAncienNumeroPublished: boolean;
  oeuvresDescriptionChampParametrableUtilisateur: string;
  oeuvresDescriptionAjoutDeMotsCles: string;
  oeuvresValidationSystemeDeNotationDeLoeuvre: number | null;
  oeuvresValidationSystemeDePublicationDeLaFicheOnlineWebFront: string;
  oeuvresValidationDescriptionOk: boolean;
  oeuvresValidationHistoriqueCollectionsOk: boolean;
  oeuvresValidationExpositionsOk: boolean;
  oeuvresValidationBibliographieOk: boolean;
  oeuvresValidationVentesOk: boolean;
  oeuvresValidationSelectionnerFiche: boolean;
  oeuvresValidationSource: string;
  oeuvresValidationOeuvreDocumentaire: boolean;
  oeuvresValidationOeuvreImportante: boolean;
  oeuvresValidationOeuvreMajeure: boolean;
  oeuvresValidationOeuvreInachevee: boolean;
  oeuvresValidationAttributionDouteuse: boolean;
  oeuvresValidationFaux: boolean;
  oeuvresValidationCopie: boolean;
  oeuvresValidationLost: boolean;
  oeuvresValidationTitleNotConfirmed: boolean;
  oeuvresValidationDestroy: boolean;
  oeuvresDescriptionRestaurationNumerique: boolean;
  oeuvresValidationStole: boolean;
  oeuvresValidationNotesDeTravail: string;
  oeuvresValidationRemarquesPubliques: string;
  oeuvresValidationRemarquesPubliquesEN: string;
  oeuvresValidationRemarquesPubliquesMultilingual: Record<
    SupportedLocale,
    string
  >;
  oeuvresValidationRemarquesPubliquesLabelPublished: boolean;
  oeuvresValidationRemarquesPersonnelles: string;
  oeuvresLocalisationHistoriqueCollectionId: string | null;
  oeuvresLocalisationHistoriqueCollectionCatalogue: string;
  oeuvresLocalisationHistoriqueCollectionNote: string;
  oeuvresLocalisationHistoriqueOeuvreNonLocalisee: boolean;
  oeuvresLocalisationHistoriqueHistoriqueNom: string;
  oeuvresLocalisationHistoriqueHistoriqueVille: string;
  oeuvresLocalisationHistoriqueHistoriqueDates: string;
  oeuvresLocalisationHistoriqueHistoriqueCollectionParticuliere: boolean;
  oeuvresLocalisationHistoriqueLieuDeStockage: string;
  oeuvresLocalisationHistoriqueNotesRangement: string;
  oeuvresLocalisationHistoriqueMarquage: boolean;
  oeuvresLocalisationHistoriqueDepotAffectionPret: boolean;
  oeuvresLocalisationHistoriqueNoteDepotAffectionPret: string;
  oeuvresLocalisationHistoriqueDateDepotAffectionPret: string;
  oeuvresLocalisationHistoriqueDateRetourDepotAffectionPret: string;
  oeuvresLocalisationHistoriqueNotes: string;
  oeuvresRegieCadre: boolean;
  oeuvresRegieDescriptionCadre: string;
  oeuvresRegieDimensionsCadre: string;
  oeuvresRegieCaisseTransport: boolean;
  oeuvresRegieDescriptionCaisse: string;
  oeuvresRegieDimensionsCaisse: string;
  oeuvresRegiePoidsCaisse: string;
  oeuvresRegiePoidsCaisseAvecOeuvre: string;
  oeuvresRegiePhotoCaisse: string;
  oeuvresRegieNotesRegie: string;
  oeuvresRegieNotesCondition: string;
  oeuvresPhotosNotesPhotos: string;
  oeuvresPhotosPhotoScanAFaire: boolean;
  oeuvresPhotosPhotoOkWeb: boolean;
  oeuvresPhotosPhotoHdOk: boolean;
  oeuvresWaitingPublication: boolean;
  oeuvresNotPublished: boolean;
  oeuvresPhotosNotesPreparationPhotos: string;
  oeuvresCreditsCreditOeuvre: string;
  oeuvresCreditsCreditPhotoOeuvre: string;
  oeuvresCreditsNatureDroits: string;
  oeuvresCreditsMontantDroits: LegacyMoneyAmount;
  oeuvresCreditsRemarqueCreditsDroits: string;
  oeuvresValorisationEstimation: string;
  oeuvresValorisationDateEstimation: string;
  oeuvresValorisationNotesEstimation: string;
  oeuvresValorisationValeurAssurance: string;
  oeuvresValorisationDateValeurAssurance: string;
  oeuvresValorisationRemarqueValeurAssurance: string;
  oeuvresDocumentsAdministratifsNotesDocumentsAdministratifs: string;
  oeuvresDocumentsAdministratifsDocumentEdite: boolean;
  oeuvresDocumentsAdministratifsLieuEtDateDocument: string;
  oeuvresDescriptionTechniqueConfirmer: boolean;
  oeuvresDescriptionSupportConfirmer: boolean;
  oeuvresDescriptionNewBdOk: boolean;
  oeuvresRangementRecolementMarquage: boolean;
  oeuvresRangementRecolementRack: string;
  oeuvresRangementRecolementPieceBox: string;
  oeuvresRangementRecolementRangee: string;
  oeuvresRangementRecolementMeuble: string;
  oeuvresRangementRecolementEtagere: string;
  oeuvresRangementRecolementBoiteCarton: string;
  oeuvresRangementRecolementClasseur: string;
  oeuvresRangementRecolementAutreReferenceRangement: string;
  oeuvresRangementRecolementDateRangement: string;
  oeuvresRangementRecolementDateMarquage: string;
  oeuvresRangementRecolementOperateurRecolement: string;
  oeuvresRangementRecolementDateRecolement: string;
  oeuvresRangementRecolementReferenceCampagneRecolement: string;
  oeuvresRangementRecolementAutreReferenceRecolement: string;
  oeuvresRangementRecolementRecolementOk: boolean;
  oeuvresAdminDocLettreInclusionEdit: boolean;
  oeuvresAdminDocCertificatExist: boolean;
  oeuvresRangementRecolementNotesRecolement: string;
  oeuvresAdministratifNotes: string;
  oeuvresRestored: boolean;
  oeuvresToRestore: boolean;

  oeuvreAchatTitre: string;
  oeuvreAchatDate: string;
  oeuvreAchatMontant?: MoneyAmount;
  oeuvreAchatMontantTVA?: MoneyAmount;
  oeuvreAchatMontantDroitsDouane?: MoneyAmount;
  oeuvreAchatMontantDroitsDeSuite?: MoneyAmount;
  oeuvreAchatMontantFraisDeVente?: MoneyAmount;

  oeuvreVenteTitre: string;
  oeuvreVenteDate: string;
  oeuvreVenteMontant?: MoneyAmount;
  oeuvreVenteMontantTVA?: MoneyAmount;
  oeuvreVenteMontantTop?: MoneyAmount;

  oeuvresPhotoProjectDescription: string;
  oeuvresPhotoProjectDescriptionPublished: boolean;
  oeuvresPhotoPeopleAndModelNames: string;
  createdAt: Date;
  updatedAt: Date | null;
}

/** Minimal type for MonetaryTransaction as returned on Artwork list (estimate only). */
export interface ArtworkMonetaryTransactionEstimate {
  id: string;
  title: string;
  date: string;
  amount: number;
  currency: string;
}

interface RelationArtwork {
  selectionPageId: string | null;
  instanceId: string;
  oeuvresLocalisationHistoriqueCollection?: Collector | null;
  relatedExhibitions: any[];
  relatedArtwork: Artwork[];
  relatedAudiovisual: Audiovisual[];
  relatedDocument: Document[];
  relatedWriting: Writing[];
  relatedPhotography: Photography[];
  relatedMultipleprint: Multipleprint[];
  relatedSculptureEditions: SculptureEdition[];
  relatedAuthor: Author[];
  artworkInExhibitions: ArtworkInExhibition[];
  artworkInAuctions: ArtworkInAuction[];
  artworkInBibliography: ArtworkInBibliography[];
  artworkHistory: ArtworkHistory[];
  relatedEnsemble: Ensemble[];
  attachments: Attachment[];
  MonetaryTransaction?: ArtworkMonetaryTransactionEstimate[];
}

export interface FetchedArtwork extends ArtworkBasic, RelationArtwork {}

export type Artwork = Overwrite<
  FetchedArtwork,
  {
    oeuvresDescriptionDimensionsOeuvre?: DimensionsDTO;
    oeuvresRegieDimensionsCadre?: DimensionsDTO;
    oeuvresRegieDimensionsCaisse?: DimensionsDTO;
    oeuvresDescriptionDimensionsHorsMarges?: DimensionsDTO;
    oeuvresSocleDimensions?: DimensionsDTO;
    oeuvresSculptureMouleDimensions?: DimensionsDTO;
  }
>;

export interface ArtworkForm extends ArtworkBasic {
  oeuvresDescriptionNomArtisteChoix: string;
  oeuvresDescriptionTitreChoix: string;
}

export type TinyArtwork = Pick<
  Artwork,
  | "id"
  | "attachments"
  | "oeuvresDescriptionTitre"
  | "oeuvresDescriptionNumeroEnregistrement"
>;

export interface SubmittedAuthenticationForm {
  id: string;
  receptionDate: Date;
  sender: string;
  type: string;
  status: string;
  isPaid?: boolean;
  images?: Array<{
    id: string;
    title: string;
    weight: number;
    downloadUrl?: string;
  }>;
  instance: {
    id: string;
  };
  documents?: Array<{
    id: string;
    title: string;
    weight: number;
    downloadUrl?: string;
  }>;
  holderName?: string;
  holderEmail?: string;
  phone?: string;
  createdAt?: Date | string;
}

export interface Attachment {
  id?: string;
  name: string;
  position: string;
  docId: string;
  caption?: LocaleDict<string>;
  legend?: string;
  legendEN?: string;
  description?: LocaleDict<string>;
  descriptionFR?: string;
  descriptionEN?: string;
  textAlt?: LocaleDict<string>;
  textAltFR?: string;
  textAltEN?: string;
  photoCredits?: string;
  notes?: LocaleDict<string> | null;
  details?: string | null;
  largePublicUrl?: string;
  mediumPublicUrl?: string;
  publicUrl?: string;
  copyrightSmallPublicUrl?: string | null;
  copyrightMediumPublicUrl?: string | null;
  copyrightLargePublicUrl?: string | null;
  order?: number;
  published: boolean;
  instanceId?: string;
  isHd?: boolean;
  /** Clé S3 de base (inchangée par une retouche). */
  link?: string;
  /** Recette courante ; null = jamais retouché. */
  editRecipe?: unknown;
  /** Only used at upload: whether to apply watermark for this image (not persisted). */
  applyWatermark?: boolean;
  /** When true, this attachment displays the instance's generic image config instead of an S3 file. */
  isGenericImage?: boolean;

  // Media kind + video/embed fields
  kind?: string; // "image" | "video" | "embed"
  videoStatus?: string; // "" | pending | processing | ready | error
  videoPublicUrl?: string | null;
  posterPublicUrl?: string | null;
  videoError?: string;
  duration?: number;
  embedProvider?: string; // "youtube" | "vimeo"
  embedVideoId?: string;
  embedUrl?: string;
  videoLoop?: boolean;
}

export interface FetchedMultipleprint {
  id: string;
  instanceId: string;
  estampesMultiplesDescriptionNumeroEnregistrement: string;
  estampesMultiplesDescriptionNumeroInventaire: string;
  estampesMultiplesDescriptionNumeroInventairePublished?: boolean;
  estampesMultiplesDescriptionNumeroAncien: string;
  estampesMultiplesDescriptionNumeroAncienPublished?: boolean;
  estampesMultiplesDescriptionNumeroAutres: string;
  estampesMultiplesDescriptionNumeroAutresPublished?: boolean;
  estampesMultiplesDescriptionAuteurArtisteAttribueA: boolean;
  estampesMultiplesDescriptionAuteurArtisteAnonyme: boolean;
  estampesMultiplesDescriptionAuteurArtisteDApres: boolean;
  estampesMultiplesDescriptionAuteurArtisteDansLeGoutDe: boolean;
  estampesMultiplesDescriptionAuteurArtisteEntourageDe: boolean;
  estampesMultiplesDescriptionAuteurAtelierDe: boolean;
  estampesMultiplesDescriptionTitre: string;
  estampesMultiplesDescriptionAutreTitre: string;
  estampesMultiplesDescriptionTitreSansTitre: boolean;
  estampesMultiplesDescriptionTitreInconnu: boolean;
  estampesMultiplesDescriptionTitreIncertin: boolean;
  estampesMultiplesDescriptionSousTitre: string;
  estampesMultiplesDescriptionDateDeCreation: string;
  estampesMultiplesDescriptionDateDeCreationInconnue: boolean;
  estampesMultiplesDescriptionDateDeCreationIncertaine: boolean;
  estampesMultiplesDescriptionDateDeCreationEstimee: boolean;
  estampesMultiplesDescriptionMotsCles: string;
  estampesMultiplesDescriptionNumerisationAFaire: boolean;
  estampesMultiplesDescriptionPhotoWebReady: boolean;
  estampesMultiplesDescriptionPhotoHdReady: boolean;
  estampesMultiplesPhotosCreditOeuvres: string;
  estampesMultiplesPhotosCreditPhotoOeuvres: string;
  estampesMultiplesPhotosCreditRemarques: string;
  estampesMultiplesDescriptionType: string;

  estampesMultiplesDescriptionTechnique: LocaleDict<string>;

  estampesMultiplesDescriptionTechniqueFRA: string;
  estampesMultiplesDescriptionTechniqueGB: string;
  estampesMultiplesDescriptionSupportMateriau: LocaleDict<string>;
  estampesMultiplesDescriptionSupportMateriauFRA: string;
  estampesMultiplesDescriptionSupportMateriauGB: string;
  estampesMultiplesDescriptionEditeur: string;
  estampesMultiplesDescriptionFabricantImprimeur: string;
  estampesMultiplesDescriptionDimensionsProfondeur: boolean | null;
  estampesMultiplesDescriptionDimensionsOeuvre: string;
  estampesMultiplesDescriptionDimensionsOeuvreRaw: string;
  estampesMultiplesDescriptionDimensionsOeuvreVerifiees: boolean;
  estampesMultiplesDescriptionDimensionsOeuvreInconnues: boolean;
  estampesMultiplesDescriptionDimensionsOeuvreDouteuses: boolean;
  estampesMultiplesDescriptionDimensionsImage: string;
  estampesMultiplesDescriptionDimensionsImageVerifiees: boolean;
  estampesMultiplesDescriptionDimensionsImageInconnues: boolean;
  estampesMultiplesDescriptionDimensionsImageDouteuses: boolean;
  estampesMultiplesDescriptionPoidsOeuvre: string;
  estampesMultiplesDescriptionOeuvreDoubleFace: boolean;
  estampesMultiplesDescriptionDescriptionComplete: string;
  estampesMultiplesDescriptionSignatureLocalisationFR: string;
  estampesMultiplesDescriptionSignatureLocalisationGB: string;
  estampesMultiplesDescriptionSignatureLocalisation: LocaleDict<string>;
  estampesMultiplesDescriptionSignatureTranscription: string;
  estampesMultiplesDescriptionInscriptionsDosFR: string;
  estampesMultiplesDescriptionInscriptionsDosEN: string;
  estampesMultiplesDescriptionInscriptionsDos: LocaleDict<string>;
  estampesMultiplesDescriptionInscriptionsDosPublished: LocaleDict<boolean>;
  estampesMultiplesDescriptionNotesPubliquesMultilingual: LocaleDict<string>;
  estampesMultiplesDescriptionTypeMultilingual: LocaleDict<string>;
  estampesMultiplesDescriptionDescriptionCompleteMultilingual: LocaleDict<string>;
  estampesMultiplesDescriptionNotesTravail: string;
  estampesMultiplesDescriptionNotesPubliques: string;
  estampesMultiplesDescriptionNotesPubliquesLabelPublished: boolean;
  sources: string;
  estampesMultiplesDescriptionResume: string;
  estampesMultiplesPublicationStatus: PublicationStatus;
  estampesMultiplesNature: MultipleprintNature;

  // Matrice
  estampesMultiplesMatriceType?: string | null;
  estampesMultiplesMatriceDimensions?: string | null;
  estampesMultiplesMatriceDimensionsRaw?: string | null;
  estampesMultiplesMatriceWeight?: string | null;
  estampesMultiplesMatriceRangement?: string | null;
  estampesMultiplesMatriceNotLocated?: boolean | null;
  estampesMultiplesMatriceDestroy?: boolean | null;
  estampesMultiplesMatriceNotPrintable?: boolean | null;
  estampesMultiplesMatriceStateNotes?: string | null;
  estampesMultiplesMatriceNotes?: string | null;
  estampesMultiplesMatriceNumeroInventaire?: string | null;
  estampesMultiplesMatriceNumeroEnregistrement?: string | null;

  createdAt: Date;
  updatedAt: Date | null;
  updatedById?: string;
  createdById?: string;
  updatedBy?: unknown;
  createdBy?: unknown;

  // Relationships
  relatedArtwork: Artwork[];
  relatedAuthor: Author[];
  relatedDocument: Document[];
  relatedWriting: Writing[];
  relatedPhotography: Photography[];
  relatedMultiplesample: Multiplesample[];
  relatedMultipleprint: Multipleprint[];
  history?: MultipleprintHistoryEntry[];
  multipleprintInAuction: MultipleprintInAuction[];
  multipleprintInExhibition: MultipleprintInExhibition[];
  multipleprintInBibliography: MultipleprintInBibliography[];
  relatedSculptureEditions: SculptureEdition[];
  relatedEnsemble: Ensemble[];
  collectorInMultipleprint: CollectorInMultipleprint[];
  multipleprintInMultipleSample: MultipleprintInMultipleSample[];
  from_Multipleprint_relatedMultipleprint: unknown[];
  /** Portfolio : ses multiples simples (avec vignettes). Vide pour un simple. */
  portfolioItems?: MultipleprintPortfolioItem[];
  /** Simple : les portfolios qui le contiennent. */
  inPortfolios?: MultipleprintPortfolioSummary[];
  externalLinks?: ExternalLink[];
  attachments: Attachment[];
}

/** Élément d'un portfolio : pièces jointes au format de l'API (`findFormattedByDocId`). */
export type MultipleprintPortfolioItem = Omit<Multipleprint, "attachments"> & {
  attachments: FormattedAttachment[];
};

export type MultipleprintPortfolioSummary = Pick<
  FetchedMultipleprint,
  | "id"
  | "estampesMultiplesDescriptionTitre"
  | "estampesMultiplesDescriptionNumeroEnregistrement"
  | "estampesMultiplesPublicationStatus"
>;

export type Multipleprint = Overwrite<
  FetchedMultipleprint,
  {
    estampesMultiplesDescriptionDimensionsOeuvre?: DimensionsDTO;
    estampesMultiplesDescriptionDimensionsImage?: DimensionsDTO;
    estampesMultiplesMatriceDimensions?: DimensionsDTO;
  }
>;

export interface Multiplesample {
  id: string;
  instanceId: string;
  exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement: string;
  exemplaireDansEstampesMultiplesDescriptionNumero: string;
  exemplaireDansEstampesMultiplesDescriptionNumeroInventaire: string;
  exemplaireDansEstampesMultiplesDescriptionNumeroInventairePubli?: boolean;
  exemplaireDansEstampesMultiplesDescriptionLocalisation: string;
  exemplaireDansEstampesMultiplesDescriptionNotes: string;
  exemplaireDansEstampesMultiplesRemarquesPubliquesMultilingual?: Record<
    SupportedLocale,
    string
  >;
  exemplaireDansEstampesMultiplesRemarquesPubliquesPublished?: boolean;
  exemplaireDansEstampesMultiplesRemarquesPubliquesLabelPubli?: boolean;
  exemplaireDansEstampesMultiplesDescriptionStatus: string;
  exemplaireDansEstampesMultiplesStatus?: string;
  multipleprints: Multipleprint[];
  mouvements?: MultiplesampleMouvement[];
  relatedAuthor?: Author[];
  attachments?: Attachment[];
}

export interface MultipleprintHistoryEntry {
  id: string;
  instanceId?: string;
  multipleprintId?: string | null;
  id_imported?: number;
  provenanceRank?: number;
  estampesMultiplesHistoriqueDescriptionNom: string;
  estampesMultiplesHistoriqueDescriptionVille: string;
  estampesMultiplesHistoriqueDescriptionDates: string;
  estampesMultiplesHistoriqueDescriptionCollectionPart: boolean;
}

export interface MultipleprintInAuction {
  id: string;
  provenanceRank?: number;
  ventesDansEstampesMultiplesDescriptionNumeroLot: string;
  ventesDansEstampesMultiplesDescriptionReproduitPage: string;
  ventesDansEstampesMultiplesDescriptionEstimationHaute: string;
  ventesDansEstampesMultiplesDescriptionEstimationBasse: string;
  ventesDansEstampesMultiplesDescriptionVendu: boolean;
  ventesDansEstampesMultiplesDescriptionResultatNonListe: boolean;
  ventesDansEstampesMultiplesDescriptionNonVendu: boolean;
  ventesDansEstampesMultiplesDescriptionNonVenu: boolean;
  ventesDansEstampesMultiplesDescriptionRetire: boolean;
  ventesDansEstampesMultiplesDescriptionMiseAPrix: string;
  ventesDansEstampesMultiplesDescriptionPrixMarteau: string;
  ventesDansEstampesMultiplesDescriptionPrixAvecFrais: string;
  ventesDansEstampesMultiplesDescriptionExemplaireVendu: string;
  multipleprint: Multipleprint;
  auction: Auction;
}

export interface Auction {
  id: string;
  instanceId: string;
  ventesDescriptionNumeroVente: string;
  ventesDescriptionCatalogue: boolean;
  ventesDescriptionTitre: string;
  ventesDescriptionDate: string;
  ventesDescriptionCommissaires: string;
  ventesDescriptionLieu: string;
  ventesDescriptionSalle: string;
  ventesDescriptionVille: string;
  ventesDescriptionPays: string;
  ventesDescriptionNotesVente: string;
  ventesDescriptionResume: string;
  ventesDescriptionInclureTitreDansResume: boolean;
  sources: string;
  ventesPublicationStatus: PublicationStatus;
  relatedBibliography: Bibliography[];
  relatedAudiovisual: Audiovisual[];
  relatedDocument: Document[];
  relatedWriting: Writing[];
  relatedPhotography: Photography[];
  artworkInAuctions: ArtworkInAuction[];
  multipleprintInAuctions: MultipleprintInAuction[];
  sculptureEditionInAuctions: SculptureEditionInAuction[];
  createdAt: Date | null;
  updatedAt: Date | null;
  updatedById?: string;
  createdById?: string;
  attachments?: Attachment[];
}

export interface FetchedBibliography {
  id: string;
  instanceId: string;
  bibliographieDescriptionNumeroBibliographie: string;
  bibliographieDescriptionAnnee: string;
  bibliographieDescriptionType: string;
  bibliographieDescriptionIllustrations: boolean;
  bibliographieDescriptionAuteur: string;
  bibliographieDescriptionTitre: string;
  bibliographieDescriptionTitreDOrigine: string;
  bibliographieDescriptionLangueDOrigine: string;
  bibliographieDescriptionSousTitre: string;
  bibliographieDescriptionCollection: string;
  bibliographieDescriptionEditeur: string;
  bibliographieDescriptionVille: string;
  bibliographieDescriptionPays: string;
  bibliographieDescriptionNomRevue: string;
  bibliographieDescriptionInclureVillePaysDansResume: boolean;
  bibliographieDescriptionNumeroRevue: string;
  bibliographieDescriptionPageArticle: string;
  bibliographieDescriptionDatesPublication: string;
  bibliographieDescriptionResumeLong: string;
  bibliographieDescriptionResumeCourt: string;
  bibliographieDescriptionRetranscription: string;
  bibliographieDescriptionNotes: string;
  bibliographieDescriptionLocalisationReference: string;
  bibliographieDescriptionRangementReference1: string;
  bibliographieDescriptionRangementReference2: string;
  bibliographieDescriptionDimensions: string; // Assuming JSON type, adjust as needed
  bibliographieDescriptionNombrePages: string;
  bibliographieDescriptionContexture: string;
  bibliographieDescriptionCouverture: string;
  bibliographieDescriptionIsbn: string;
  bibliographieDescriptionAutreCodeBibliographie: string;
  bibliographieValidationReprosOeuvresOk: boolean;
  bibliographieValidationReprosPhotosOk: boolean;
  bibliographieValidationReprosDocumentsOk: boolean;
  bibliographieValidationListeDesOeuvresOk: boolean;
  bibliographieValidationPublicationNumerisee: boolean;
  bibliographieValidationPdfEtOcrOk: boolean;
  bibliographieValidationNoticeBibliographiqueOk: boolean;
  sources: string;
  bibliographiePublicationStatus: PublicationStatus;
  relatedAuthor: Author[];
  attachments: Attachment[];
  sculptureEditionInBibliography: SculptureEditionInBibliography[];
  updatedAt: Date | null;
  createdAt: Date | null;
  updatedById?: string;
  createdById?: string;
}

export type Bibliography = Overwrite<
  FetchedBibliography,
  {
    bibliographieDescriptionDimensions?: DimensionsDTO;
  }
>;

export interface MultipleprintInExhibition {
  id: string;
  multipleprint: Multipleprint;
  exhibition: Exhibition;
  expositionsDansEstampesMultiplesDescriptionNumero: string;
  expositionsDansEstampesMultiplesDescriptionExemplaireExpose: string;
  itinerantGroupId?: string | null;
  itinerantOrder?: number | null;
  itinerantNote?: string;
  attachments?: Attachment[];
}

export interface MultipleprintInBibliography {
  id: string;
  multipleprint: Multipleprint;
  bibliography: Bibliography;
  bibliographieDansEstampesMultiplesDescriptionReproduitPage: string;
  bibliographieDansEstampesMultiplesDescriptionReproduitPlanche: string;
  biblioInMultiPrintDescPlancheNonNum: boolean;
  bibliographieDansEstampesMultiplesDescriptionCitePage: string;
  bibliographieDansEstampesMultiplesDescriptionListePage: string;
  bibliographieDansEstampesMultiplesDescriptionSousNumero: string;
  bibliographieDansEstampesMultiplesDescriptionPhotographe: string;
  bibliographieDansEstampesMultiplesDescriptionReproductionCoul: boolean;
  biblioInMultiPrintDescReproCover: boolean;
  bibliographieDansEstampesMultiplesDescriptionNonPagine: boolean;
  bibliographieDansEstampesMultiplesDescriptionTexteLibre: string;
  bibliographieDansEstampesMultiplesDescriptionExemplaireRepr: string;
  attachments?: Attachment[];
}

export interface MultipleSampleInExhibition {
  id: string;
  multiplesample: Multiplesample;
  exhibition: Exhibition;
  multiplesampleDansExpositionsDescriptionNumeroCatalogue: string;
  multiplesampleDansExpositionsNotes: string;
  itinerantGroupId?: string | null;
  itinerantOrder?: number | null;
  itinerantNote?: string;
  attachments?: Attachment[];
}

export interface MultipleSampleInBibliography {
  id: string;
  multiplesample: Multiplesample;
  bibliography: Bibliography;
  multiplesampleDansBibliographieDescriptionReproduitPage: string;
  multiplesampleDansBibliographieDescriptionReproduitPlanche: string;
  multiplesampleDansBibliographieDescriptionPlancheNonNumerotee: boolean;
  multiplesampleDansBibliographieDescriptionCitePage: string;
  multiplesampleDansBibliographieDescriptionListePage: string;
  multiplesampleDansBibliographieDescriptionSousNumero: string;
  multiplesampleDansBibliographieDescriptionPhotographe: string;
  multiplesampleDansBibliographieDescriptionReproductionCouleur: boolean;
  multiSampleInBiblioDescReproCover: boolean;
  multiplesampleDansBibliographieDescriptionNonPagine: boolean;
  multiplesampleDansBibliographieDescriptionTexteLibre: string;
  multiplesampleDansBibliographieNotes: string;
  attachments?: Attachment[];
}

export interface MultipleSampleInAuction {
  id: string;
  provenanceRank?: number;
  multiplesample: Multiplesample;
  auction: Auction;
  multiplesampleDansVentesDescriptionNumeroLot: string;
  multiplesampleDansVentesDescriptionReproduitPage: string;
  multiplesampleDansVentesDescriptionEstimationHaute: string;
  multiplesampleDansVentesDescriptionEstimationBasse: string;
  multiplesampleDansVentesDescriptionVendu: boolean;
  multiplesampleDansVentesDescriptionResultatNonListe: boolean;
  multiplesampleDansVentesDescriptionNonVenu: boolean;
  multiplesampleDansVentesDescriptionNonVendu: boolean;
  multiplesampleDansVentesDescriptionNonReproduit: boolean;
  multiplesampleDansVentesDescriptionRetire: boolean;
  multiplesampleDansVentesDescriptionMiseAPrix: string;
  multiplesampleDansVentesDescriptionPrixMarteau: string;
  multiplesampleDansVentesDescriptionPrixAvecFrais: string;
  multiplesampleDansVentesDescriptionNotes: string;
  attachments?: Attachment[];
}

export interface CollectorInMultipleprint {
  id: string;
  collector: Collector;
  multipleprint: Multipleprint;
  estampesDansCollectionneursDescriptionInfos: string;
  attachments?: Attachment[];
}

export interface MultipleprintInMultipleSample {
  id: string;
  instanceId: string;
  multipleprint: Multipleprint;
  multiplesample: Multiplesample;
}

export interface Author {
  id: string;
  instanceId: string;
  artistesAuteursDescriptionPrenom: string;
  artistesAuteursDescriptionNom: string;
  artistesAuteursDescriptionEtatCivil: string;
  artistesAuteursDescriptionDateNaissance: string;
  artistesAuteursDescriptionVilleDeNaissance: string;
  artistesAuteursDescriptionDateDeDeces: string;
  artistesAuteursDescriptionVilleDeDeces: string;
  artistesAuteursDescriptionNationalite: string;
  artistesAuteursDescriptionResumeDates: string;
  artistesAuteursDescriptionPaysDActivite: string;
  artistesAuteursDescriptionActivite: string;
  artistesAuteursDescriptionPeriodeDactivit: string;
  artistesAuteursDescriptionNotesDeTravail: string;
  artistesAuteursDescriptionNotesBiographiques: string;
  artistesAuteursAyantDroitsNom: string;
  artistesAuteursAyantDroitsContact: string;
  artistesAuteursAyantDroitsLienAvecLAuteur: string;
  artistesAuteursAyantDroitsOrganismeGerantLesDroits: string;
  artistesAuteursExpertCrNom: string;
  artistesAuteursExpertCrContact: string;
  artistesAuteursExpertCrCatalogueRaisonne: boolean;
  artistesAuteursExpertCrEtatDuCatalogueRaisonne: string;
  artistesAuteursExpertCrDescriptionDuCatalogueRaisonne: string;
  artistesAuteursPublicationStatus: PublicationStatus;
  sources: string;
  createdAt: Date;
  updatedAt: Date | null;
  updatedById: string | null;
  createdById: string | null;
  relatedArtwork: Artwork[];
  relatedBibliography: Bibliography[];
  relatedMultipleprint: Multipleprint[];
}

export interface ArtworkInExhibition {
  id: string;
  artwork: Artwork;

  exhibition: Exhibition;
  oeuvresDansExpositionsDescriptionNumeroCatalogue: string;
  oeuvresDansExpositionsNotes: string;
  itinerantGroupId?: string | null;
  itinerantOrder?: number | null;
  itinerantNote?: string;
  attachments?: Attachment[];
}

export type ItinerantExhibitionParentType =
  | "artwork"
  | "tirage"
  | "multipleprint"
  | "multiplesample"
  | "sculptureEdition"
  | "audiovisual"
  | "bibliography"
  | "document"
  | "photography"
  | "writing"
  | "exhibition";

export interface ItinerantExhibitionStopInput {
  linkId?: string;
  exhibitionId: string;
  catalogNumber?: string;
  exemplaireExpose?: string;
  note?: string;
  order: number;
}

export interface SyncItinerantExhibitionGroupInput {
  groupId?: string;
  parentType: ItinerantExhibitionParentType;
  parentId: string;
  stops: ItinerantExhibitionStopInput[];
}

export interface ArtworkInAuction {
  id: string;
  artwork: Artwork;
  auction: Auction;
  oeuvresDansVentesDescriptionNumeroLot: string;
  oeuvresDansVentesDescriptionReproduitPage: string;
  oeuvresDansVentesDescriptionEstimationHaute: string;
  oeuvresDansVentesDescriptionEstimationBasse: string;
  oeuvresDansVentesDescriptionVendu: boolean;
  oeuvresDansVentesDescriptionResultatNonListe: boolean;
  oeuvresDansVentesDescriptionNonVendu: boolean;
  oeuvresDansVentesDescriptionNonVenu: boolean;
  oeuvresDansVentesDescriptionNonReproduit: boolean;
  oeuvresDansVentesDescriptionRetire: boolean;
  oeuvresDansVentesDescriptionMiseAPrix: string;
  oeuvresDansVentesDescriptionPrixMarteau: string;
  oeuvresDansVentesDescriptionPrixAvecFrais: string;
  oeuvresDansVentesDescriptionNotes: string;
  attachments?: Attachment[];
}

export interface ArtworkInBibliography {
  id: string;
  artwork: Artwork;
  bibliography: Bibliography;
  oeuvresDansBibliographieDescriptionReproduitPage: string;
  oeuvresDansBibliographieDescriptionReproduitPlanche: string;
  oeuvresDansBibliographieDescriptionPlancheNonNumerotee: boolean;
  oeuvresDansBibliographieDescriptionCitePage: string;
  oeuvresDansBibliographieDescriptionListePage: string;
  oeuvresDansBibliographieDescriptionSousNumero: string;
  oeuvresDansBibliographieDescriptionPhotographe: string;
  oeuvresDansBibliographieDescriptionReproductionCouleur: boolean;
  oeuvresDansBibliographieDescriptionNonPagine: boolean;
  oeuvresDansBibliographieDescriptionReproductionCouverture: boolean;
  oeuvresDansBibliographieDescriptionTexteLibre: string;
  oeuvresDansBibliographieNotes: string;
  attachments?: Attachment[];
}

export interface TirageInExhibition {
  id: string;
  tirage: Tirage;
  exhibition: Exhibition;
  tirageDansExpositionsDescriptionNumeroCatalogue: string;
  tirageDansExpositionsNotes: string;
  itinerantGroupId?: string | null;
  itinerantOrder?: number | null;
  itinerantNote?: string;
  attachments?: Attachment[];
}

export interface TirageInAuction {
  id: string;
  provenanceRank?: number;
  tirage: Tirage;
  auction: Auction;
  tirageDansVentesDescriptionNumeroLot: string;
  tirageDansVentesDescriptionReproduitPage: string;
  tirageDansVentesDescriptionEstimationHaute: string;
  tirageDansVentesDescriptionEstimationBasse: string;
  tirageDansVentesDescriptionVendu: boolean;
  tirageDansVentesDescriptionResultatNonListe: boolean;
  tirageDansVentesDescriptionNonVenu: boolean;
  tirageDansVentesDescriptionNonVendu: boolean;
  tirageDansVentesDescriptionNonReproduit: boolean;
  tirageDansVentesDescriptionRetire: boolean;
  tirageDansVentesDescriptionMiseAPrix: string;
  tirageDansVentesDescriptionPrixMarteau: string;
  tirageDansVentesDescriptionPrixAvecFrais: string;
  tirageDansVentesDescriptionNotes: string;
  attachments?: Attachment[];
}

export interface TirageInBibliography {
  id: string;
  tirage: Tirage;
  bibliography: Bibliography;
  tirageDansBibliographieDescriptionReproduitPage: string;
  tirageDansBibliographieDescriptionReproduitPlanche: string;
  tirageDansBibliographieDescriptionPlancheNonNumerotee: boolean;
  tirageDansBibliographieDescriptionCitePage: string;
  tirageDansBibliographieDescriptionListePage: string;
  tirageDansBibliographieDescriptionSousNumero: string;
  tirageDansBibliographieDescriptionPhotographe: string;
  tirageDansBibliographieDescriptionReproductionCouleur: boolean;
  tirageDansBibliographieDescriptionReproductionCouverture: boolean;
  tirageDansBibliographieDescriptionNonPagine: boolean;
  tirageDansBibliographieDescriptionTexteLibre: string;
  tirageDansBibliographieNotes: string;
  attachments?: Attachment[];
}

export type ArtworkHistory = {
  id: string;
  instanceId: string;
  oeuvresLocalisationHistoriqueHistoriqueNom: string;
  oeuvresLocalisationHistoriqueHistoriqueVille: string;
  oeuvresLocalisationHistoriqueHistoriqueDates: string;
  oeuvresLocalisationHistoriqueHistoriqueCollectionParticuliere: boolean;
  artwork?: Artwork;
  updatedAt?: Date;
  createdAt: Date;
};

export interface Ensemble {
  id: string;
  ensemblesDescriptionNomEnsemble: string;
  ensemblesDescriptionType?: string;
  ensemblesDescriptionDateCreationEnsemble?: Date;
  ensemblesDescriptionNotesEnsemble: string;
  visibility?: "private" | "shared";
  artworkCount: number;

  ensemblesText1Document1?: string;
  ensemblesText1Document2?: string;
  ensemblesText1Document3?: string;
  ensemblesText2Document1?: string;
  ensemblesText2Document2?: string;
  ensemblesText2Document3?: string;
  ensemblesTitreDocument1?: string;
  ensemblesTitreDocument2?: string;
  ensemblesTitreDocument3?: string;
}

// More complete version of Ensemble, that appears in the Settings page
export interface PermanentEnsemble {
  id: string;
  ensemblesDescriptionNomEnsemble: string;
  ensemblesDescriptionType?: string;
  ensemblesDescriptionAuteurEnsemble?: string;
  ensemblesDescriptionOperateurPrincipalEnsemble?: string;
  ensemblesDescriptionOperateurAutre?: string;
  ensemblesDescriptionSignataire?: string;
  ensemblesDescriptionTitreDocumentAdministratif?: string;
  ensemblesDescriptionNotesEnsemble?: string;
  ensemblesCoordonneesAuteurAdresse1?: string;
  ensemblesCoordonneesAuteurAdresse2?: string;
  ensemblesCoordonneesAuteurCodePostal?: string;
  ensemblesCoordonneesAuteurVille?: string;
  ensemblesCoordonneesAuteurPays?: string;
  ensemblesCoordonneesAuteurTelephone?: string;
  ensemblesCoordonneesAuteurMail?: string;
  ensemblesCoordonneesAuteurSiteWeb?: string;
  ensemblesDescriptionRangement?: string;
  ensemblesLegalsText?: string;
  ensemblesTitreDocument1?: string;
  ensemblesText1Document1?: string;
  ensemblesText2Document1?: string;
  ensemblesTitreDocument2?: string;
  ensemblesText1Document2?: string;
  ensemblesText2Document2?: string;
  ensemblesTitreDocument3?: string;
  ensemblesText1Document3?: string;
  ensemblesText2Document3?: string;
}

export interface LegalPageDTO {
  title: string;
  content: string;
}

export interface Exhibition {
  id: string;
  instanceId: string;
  expositionsDescriptionExpositionPersonnelleDeId: string | null;
  expositionsDescriptionAutresArtistes: string;
  expositionsDescriptionResume: string;
  expositionsPublicationStatus: PublicationStatus;
  sources: string;
  expositionsDescriptionNumeroExpo: string;
  expositionsDescriptionAnnee: string;
  expositionsDescriptionTitre: string;
  expositionsDescriptionTitreDOrigine: string;
  expositionsDescriptionDates: string;
  expositionsDescriptionLieu: string;
  expositionsDescriptionVille: string;
  expositionsDescriptionPays: string;
  expositionsDescriptionTexteSupplementaire: string;
  expositionsDescriptionOrganisateur: string;
  expositionsDescriptionNotes: string;
  expositionsDescriptionExpositionItinerante: boolean;
  expositionsDescriptionExpositionItinerante1EreExpo: string;
  expositionsDescriptionExpositionDansMusee: boolean;
  expositionsDescriptionExpositionCollective: boolean;
  expositionsDescriptionAnneeIncertaine: boolean;
  expositionsDescriptionTitreIncertain: boolean;
  expositionsDescriptionDatesChoixIncertaines: boolean;
  expositionsDescriptionDatesChoixInconnues: boolean;
  expositionsDescriptionLieuIncertain: boolean;
  expositionsDescriptionCityIncertain: boolean;
  expositionsDescriptionCountryIncertain: boolean;
  expositionsDescriptionExpositionPersonnelle: boolean;
  relatedExhibition: Exhibition[];
  from_Exhibition_relatedExhibition: Exhibition[];
  relatedBibliography: Bibliography[];
  relatedAudiovisual: Audiovisual[];
  relatedDocument: Document[];
  relatedWriting: Writing[];
  relatedPhotography: Photography[];
  artworkInExhibitions: ArtworkInExhibition[];
  multipleprintInExhibitions: MultipleprintInExhibition[];
  sculptureEditionInExhibitions: SculptureEditionInExhibition[];
  updatedAt: Date | null;
  createdAt: Date | null;
  updatedById: string | null;
  createdById: string | null;
  attachments: Attachment[];
}

export interface Collector {
  id: string;
  instanceId: string;
  collectionneursDescriptionPrenom: string;
  collectionneursDescriptionNom: string;
  collectionneursDescriptionNomTri: string;
  collectionneursDescriptionVille: string;
  collectionneursDescriptionVillePublished: boolean;
  collectionneursDescriptionCivilite: string;
  collectionneursDescriptionType: string;
  collectionneursDescriptionLieu: string;
  collectionneursDescriptionRue: string;
  collectionneursDescriptionCodePostal: string;
  collectionneursDescriptionPays: string;
  collectionneursDescriptionPaysPublished: boolean;
  collectionneursDescriptionCollectionPublique: boolean;
  collectionneursDescriptionNomPublic: string;
  collectionneursDescriptionNomContact: string;
  collectionneursDescriptionTelephoneMobile: string;
  collectionneursDescriptionTelephone: string;
  collectionneursDescriptionAdresseMail: string;
  collectionneursDescriptionSiteWeb: string;
  collectionneursDescriptionNotes: string;
  collectionneursPublicationStatus: PublicationStatus;
  relatedArtwork: Artwork[];
  relatedTirage: Tirage[];
  relatedSculptureEditions: SculptureEdition[];
  collectorInMultipleprints: CollectorInMultipleprint[];
  createdAt: Date | null;
  updatedAt: Date | null;
  updatedById?: string;
  createdById?: string;
}

export interface Audiovisual {
  id: string;
  instanceId: string;
  audiovisuelDescriptionNumeroEnregistrement: string;
  audiovisuelDescriptionAuteur: string;
  audiovisuelDescriptionAuteurAnonyme: boolean;
  audiovisuelDescriptionTitre: string;
  audiovisuelDescriptionTitreSansTitre: boolean;
  audiovisuelDescriptionTitreInconnu: boolean;
  audiovisuelDescriptionEmission: string;
  audiovisuelDescriptionSousTitre: string;
  audiovisuelDescriptionDateDeCreation: string;
  audiovisuelDescriptionDateDeCreationInconnue: boolean;
  audiovisuelDescriptionDateDe1EreDiffusion: string;
  audiovisuelDescriptionDateDe1EreDiffusionInconnue: boolean;
  audiovisuelDescriptionType: string;
  audiovisuelDescriptionRealisation: string;
  audiovisuelDescriptionProduction: string;
  audiovisuelDescriptionDiffusion: string;
  audiovisuelDescriptionDistribution: string;
  audiovisuelDescriptionPays: string;
  audiovisuelDescriptionResume: string;
  audiovisuelDescriptionTechniqueDuree: string;
  audiovisuelDescriptionTechniqueFormatCineVideo: string;
  audiovisuelDescriptionTechniqueCouleur: boolean;
  audiovisuelDescriptionTechniqueFormatSon: string;
  audiovisuelDescriptionTechniqueVersionLangue: string;
  audiovisuelDescriptionTechniqueSousTitre: string;
  audiovisuelDescriptionTechniqueAssistantRalisation: string;
  audiovisuelDescriptionTechniqueMontage: string;
  audiovisuelDescriptionTechniqueImage: string;
  audiovisuelDescriptionTechniqueMusique: string;
  audiovisuelDescriptionTechniqueSon: string;
  audiovisuelDescriptionTechniqueAutresPostesTechniques: string;
  audiovisuelDescriptionTechniqueActeur: string;
  audiovisuelDescriptionTechniqueRole: string;
  audiovisuelDescriptionNotesDescriptionFilm: string;
  audiovisuelDescriptionNotesDeTravail: string;
  sources: string;
  audiovisuelPublicationStatus: PublicationStatus;
  relatedArtwork: Artwork[];
  relatedExhibition: Exhibition[];
  relatedBibliography: Bibliography[];
  relatedAuction: Auction[];
  relatedDocument: Document[];
  relatedPhotography: Photography[];
  relatedSculptureEditions: SculptureEdition[];
  attachments: Attachment[];
  relatedWriting: Writing[];
  createdAt: Date | null;
  updatedAt: Date | null;
  updatedById?: string;
  createdById?: string;
}

export interface FetchedDocument {
  id: string;
  instanceId: string;
  documentsDescriptionNumeroEnregistrement: string;
  documentsDescriptionTitre: string;
  documentsDescriptionLangueDOrigine: string;
  documentsDescriptionType: string;
  documentsDescriptionClassement: string;
  documentsDescriptionRangement: string;
  documentsDescriptionLocalisation: string;
  documentsDescriptionNombreExemplairesArchives: string;
  documentsDescriptionTheme: string;
  documentsDescriptionDate: string;
  documentsDescriptionDimensions: string;
  documentsDescriptionTechnique: string;
  documentsDescriptionLieuDeCreation: string;
  documentsDescriptionPhotographe: string;
  documentsDescriptionCopyright: string;
  documentsDescriptionCopies: boolean;
  documentsDescriptionNombreFeuillets: string;
  documentsDescriptionPhotoAutresPages: string;
  documentsDescriptionLegendePhotoAutresPages: string;
  documentsDescriptionLegendeRedigee: string;
  documentsDescriptionNotes: string;
  documentsDescriptionRetranscription: string;
  documentsFichierPhotoBasseDefinitionUniquement: boolean;
  documentsFichierPhotoScanAFaire: boolean;
  documentsFichierPhotoOkWeb: boolean;
  documentsFichierPhotoHdOk: boolean;
  documentsValidationPublicationNumerisee: boolean;
  documentsValidationPdfEtOcrOk: boolean;
  documentsAcquisitionDocumentAAcquerir: boolean;
  documentsAcquisitionDocumentAcquis: boolean;
  documentsAcquisitionDateAcquisition: string;
  documentsAcquisitionVendeur: string;
  documentsAcquisitionNotes: string;
  documentsMouvementsDepot: string;
  documentsMouvementsNotesDepot: string;
  documentsMouvementsValeurAssurance: string;
  sources: string;
  documentsPublicationStatus: PublicationStatus;
  relatedArtwork: Artwork[];
  relatedExhibition: Exhibition[];
  relatedAuction: Auction[];
  relatedAudiovisual: Audiovisual[];
  relatedWriting: Writing[];
  relatedSculptureEditions: SculptureEdition[];
  documentInBibliography: DocumentInBibliography[];
  attachments: Attachment[];
  createdAt: Date | null;
  updatedAt: Date | null;
  updatedById?: string;
  createdById?: string;
}

export type Document = Overwrite<
  FetchedDocument,
  {
    documentsDescriptionDimensions?: DimensionsDTO;
  }
>;

export interface FetchedWriting {
  id: string;
  instanceId: string;
  ecritsDescriptionNumeroEnregistrement: string;
  ecritsDescriptionTitre: string;
  ecritsDescriptionLangueDOrigine: string;
  ecritsDescriptionType: string;
  ecritsDescriptionClassement: string;
  ecritsDescriptionRangement: string;
  ecritsDescriptionLocalisation: string;
  ecritsDescriptionNombreExemplairesArchives: string;
  ecritsDescriptionTheme: string;
  ecritsDescriptionDate: string;
  ecritsDescriptionDimensions: string;
  ecritsDescriptionTechnique: string;
  ecritsDescriptionLieuDeCreation: string;
  ecritsDescriptionPhotographe: string;
  ecritsDescriptionCopyright: string;
  ecritsDescriptionCopies: boolean;
  ecritsDescriptionNombreFeuillet: string;
  ecritsDescriptionLegendeRedigee: string;
  ecritsDescriptionNotes: string;
  ecritsDescriptionRetranscription: string;
  ecritsFichierPhotoBasseDefinitionUniquement: boolean;
  ecritsFichierPhotoScanAFaire: boolean;
  ecritsFichierPhotoOkWeb: boolean;
  ecritsFichierPhotoHdOk: boolean;
  ecritsValidationPublicationNumerisee: boolean;
  ecritsValidationPdfEtOcrOk: boolean;
  ecritsAcquisitionDocumentAAcquerir: boolean;
  ecritsAcquisitionDocumentAAcquis: boolean;
  ecritsAcquisitionDateAcquisition: string;
  ecritsAcquisitionVendeur: string;
  ecritsAcquisitionNotes: string;
  ecritsMouvementsDepot: string;
  ecritsMouvementsNotesDepot: string;
  ecritsMouvementsValeurAssurance: string;
  ecritsPublicationStatus: PublicationStatus;
  sources: string;
  relatedArtwork: Artwork[];
  relatedExhibition: Exhibition[];
  relatedBibliography?: FetchedBibliography[];
  relatedAuction: Auction[];
  relatedAudiovisual: Audiovisual[];
  relatedDocument?: FetchedDocument[];
  relatedSculptureEditions: SculptureEdition[];
  createdAt: Date | null;
  updatedAt: Date | null;
  updatedById?: string;
  createdById?: string;
  attachments: Attachment[];
}

export type Writing = Overwrite<
  FetchedWriting,
  {
    ecritsDescriptionDimensions?: DimensionsDTO;
    relatedBibliography?: Bibliography[];
    relatedDocument?: Document[];
  }
>;

export interface FetchedPhotography {
  id: string;
  instanceId: string;
  photosDescriptionNumeroEnregistrement: string;
  photosDescriptionType: string;
  photosDescriptionClassement: string;
  photosDescriptionRangement: string;
  photosDescriptionLocalisation: string;
  photosDescriptionNombreExemplairesArchives: string;
  photosDescriptionTitre: string;
  photosDescriptionTheme: string;
  photosDescriptionDate: string;
  photosDescriptionDimensions: string;
  photosDescriptionTechnique: string;
  photosDescriptionLieuDeCreation: string;
  photosDescriptionPhotographe: string;
  photosDescriptionCopyright: string;
  photosDescriptionCopies: boolean;
  photosDescriptionPhoto: string;
  photosDescriptionPhotoDos: string;
  photosDescriptionPhotoVariante: string;
  photosDescriptionPhotoVarianteLegende: string;
  photosDescriptionLegendeRedigee: string;
  photosDescriptionNotes: string;
  photosDescriptionPersonnes: string;
  photosFichierPhotoBasseDefinitionUniquement: boolean;
  photosFichierPhotoScanAFaire: boolean;
  photosFichierPhotoOkWeb: boolean;
  photosFichierPhotoHdOk: boolean;
  photosAcquisitionDocumentAAcquerir: boolean;
  photosAcquisitionDocumentAcquis: boolean;
  photosAcquisitionDateAcquisition: string;
  photosAcquisitionVendeur: string;
  photosAcquisitionNotes: string;
  photosMouvementsDepot: string;
  photosMouvementsNotesDepot: string;
  photosMouvementsValeurAssurance: string;
  photosPublicationStatus: PublicationStatus;
  sources: string;
  relatedArtwork: Artwork[];
  relatedExhibition: Exhibition[];
  relatedAuction: Auction[];
  relatedAudiovisual: Audiovisual[];
  relatedSculptureEditions: SculptureEdition[];
  photographyInBibliography: PhotographyInBibliography[];
  createdAt: Date | null;
  updatedAt: Date | null;
  updatedById?: string;
  createdById?: string;
  attachments: Attachment[];
}

export type Photography = Overwrite<
  FetchedPhotography,
  {
    photosDescriptionDimensions?: DimensionsDTO;
  }
>;

export type TinyPhotography = Pick<
  Photography,
  | "id"
  | "attachments"
  | "photosDescriptionTitre"
  | "photosDescriptionNumeroEnregistrement"
>;
export interface DocumentInBibliography {
  id: string;
  document: Document;
  bibliography: Bibliography;
  documentsDansBibliographieDescriptionReproduitPage: string;
  documentsDansBibliographieDescriptionCitePage: string;
  documentsDansBibliographieDescriptionPhotographe: string;
  documentsDansBibliographieDescriptionReproductionCouleur: boolean;
  documentsDansBibliographieDescriptionNonPagine: boolean;
  attachments?: Attachment[];
}

/**
 * Lien complexe auto-référentiel Biblio ↔ Biblio (bidirectionnel).
 * `bibliography` = la fiche liée (l'autre côté, résolu côté API).
 */
export interface BibliographyInBibliography {
  id: string;
  bibliography: Bibliography;
  bibliographieDansBibliographieDescriptionPage: string;
  bibliographieDansBibliographieDescriptionReproduitPage: string;
  bibliographieDansBibliographieDescriptionCitePage: string;
  bibliographieDansBibliographieDescriptionListePage: string;
  bibliographieDansBibliographieDescriptionNonPagine: boolean;
  bibliographieDansBibliographieDescriptionReproductionCouverture: boolean;
  bibliographieDansBibliographieDescriptionReproductionCouleur: boolean;
  bibliographieDansBibliographieDescriptionTextePersonnalise: string;
  attachments?: Attachment[];
}

export interface PhotographyInBibliography {
  id: string;
  photography: Photography;
  bibliography: Bibliography;
  photographiesDansBibliographieDescriptionReproduitPage: string;
  photographiesDansBibliographieDescriptionCitePage: string;
  photographiesDansBibliographieDescriptionPhotographe: string;
  photographiesDansBibliographieDescriptionReproductionCouleur: boolean;
  photographiesDansBibliographieDescriptionNonPagine: boolean;
  attachments?: Attachment[];
}

export interface ModifiableList {
  id: string;
  instanceId: string;
  listKey: string;
  lang: SupportedLocale;
  value: string;
  order: number;
  isLocked?: boolean;
  createdAt: Date | string;
  updatedAt?: Date | string;
}

export interface Trash {
  id?: string;
  documentId?: string;
  documentType?: string;
  title?: string;
  instanceId?: string;
}

export interface Forfait {
  id: string;
  clientName: string;
  email: string;
  maxArtworks: number;
  maxUsers: number;
  maxWeight: number;
  name: string;
  numberArtworks: number;
  numberUsers: number;
  phone: string;
  subscribedOptions: Option[];
  subscriptionStart: Date;
  usedSpaceInGB: number;
  hdOption: boolean;
  users: {
    user: {
      name: string;
      lastName: string;
    };
  }[];
}

export interface Option {
  id: string;
  name: string;
  description: string;
  code: string;
  tooltip: string;
  price: number;
  maxWeight?: number;
  type: string;
}

export type ArtworkRelation = {
  tasteOf: boolean;
  attributedTo: boolean;
  from: boolean;
  entourageOf: boolean;
  studioOf: boolean;
};

export type TitleState = {
  unknown: boolean;
  none: boolean;
  notConfirmed: boolean;
};

/**
 *
 * @param str This returns undefined, so that zod accepts fields with undefined values.
 * @returns
 */
export const emptyStringToNull = (
  str?: string | null | undefined,
): string | null | undefined => str?.trim() || null;

export const emptyToNullString = (p?: Parameters<typeof z.string>[0]) =>
  z.string(p).optional().nullable().transform(emptyStringToNull);

export type TupleOf<
  T,
  N extends number,
  R extends T[] = [],
> = R["length"] extends N ? R : TupleOf<T, N, [T, ...R]>;

export type ImageSize = {
  width: number;
  height: number;
};

const authorCoreInfoSchema = z.object({
  artistesAuteursDescriptionNom: z.string(),
  artistesAuteursDescriptionPrenom: z.string(),
  artistesAuteursDescriptionDateDeDeces: z.string(),
  artistesAuteursDescriptionDateNaissance: z.string(),
  artistesAuteursDescriptionResumeDates: z.string(),
  artistesAuteursDescriptionPaysDActivite: z.string(),
});

export type AuthorCoreInfo = z.infer<typeof authorCoreInfoSchema>;

const dimensionsSchema = z.object({
  cm_width: z.number(),
  in_width: z.string(),
  cm_height: z.number(),
  in_height: z.string(),
  cm_depth: z.number(),
  in_depth: z.string(),
  cm_volume: z.number(),
  in_volume: z.string(),
  cm_surface: z.number(),
  in_surface: z.string(),
  cm_diameter: z.number(),
  in_diameter: z.string(),
});

export type Dimensions = z.infer<typeof dimensionsSchema>;

export const artworkCoreInfoSchema = z.object({
  imageType: z.literal("artwork"),
  id: z.string(),

  // Title
  oeuvresDescriptionTitre: z.string(),
  oeuvresDescriptionTitreInconnu: z.boolean(),
  oeuvresDescriptionTitreSansTitre: z.boolean(),
  oeuvresDescriptionSousTitre: z.string(),
  oeuvresDescriptionAutreTitre: z.string(),
  oeuvresValidationTitleNotConfirmed: z.boolean(),

  // Technique / Support
  oeuvresDescriptionSupport: multilingualFieldSchema(z.string()),
  oeuvresDescriptionTechnique: multilingualFieldSchema(z.string()),
  oeuvresDescriptionOeuvreDoubleFace: z.boolean(),

  // Dimensions
  oeuvresDescriptionDimensionsOeuvre: dimensionsSchema,
  oeuvresDescriptionDimensionsOeuvreInconnues: z.boolean(),

  // Back Text
  oeuvresDescriptionInscriptionsDosMultilingual: multilingualFieldSchema(
    z.string(),
  ),

  // Signature
  oeuvresDescriptionSignatureLocalisation: multilingualFieldSchema(z.string()),
  oeuvresDescriptionSignatureTranscription: z.string(),

  // Collection
  oeuvresLocalisationHistoriqueCollectionCatalogue: z.string(),
  oeuvresLocalisationHistoriqueCollection: z
    .object({
      collectionneursDescriptionPrenom: z.string(),
      collectionneursDescriptionNom: z.string(),
      collectionneursDescriptionNomTri: z.string(),
      collectionneursDescriptionVille: z.string(),
      collectionneursDescriptionCivilite: z.string(),
      collectionneursDescriptionLieu: z.string(),
      collectionneursDescriptionCollectionPublique: z.boolean(),
    })
    .optional(),

  // Creation Date
  oeuvresDescriptionDateDeCreation: z.string(),
  oeuvresDescriptionDateDeCreationInconnue: z.boolean(),
  oeuvresDescriptionDateDeCreationIncertaine: z.boolean(),
  oeuvresDescriptionDateDeCreationEstimee: z.boolean(),

  // Registration Number
  oeuvresDescriptionNumeroEnregistrement: z.string(),

  // Authors
  relatedAuthors: z.array(authorCoreInfoSchema),

  // Face media (image / vidéo / embed)
  url: z.string(),
  urlLarge: z.string().optional(),
  kind: z.string().optional(),
  videoPublicUrl: z.string().optional().nullable(),
  posterPublicUrl: z.string().optional().nullable(),
  embedProvider: z.string().optional(),
  embedVideoId: z.string().optional(),
  embedUrl: z.string().optional(),
  videoLoop: z.boolean().optional(),
});

export type ArtworkCoreInfo = z.infer<typeof artworkCoreInfoSchema>;

export const multipleprintCoreInfoSchema = z.object({
  imageType: z.literal("multipleprint"),
  id: z.string(),

  // Title
  estampesMultiplesDescriptionTitre: z.string(),
  estampesMultiplesDescriptionTitreInconnu: z.boolean(),
  estampesMultiplesDescriptionTitreSansTitre: z.boolean(),
  estampesMultiplesDescriptionSousTitre: z.string(),
  estampesMultiplesDescriptionTitreIncertin: z.boolean(),

  // Technique / Support
  estampesMultiplesDescriptionSupportMateriau: multilingualFieldSchema(
    z.string(),
  ),
  estampesMultiplesDescriptionTechnique: multilingualFieldSchema(z.string()),
  estampesMultiplesDescriptionOeuvreDoubleFace: z.boolean(),

  // Dimensions
  estampesMultiplesDescriptionDimensionsOeuvre: dimensionsSchema,
  estampesMultiplesDescriptionDimensionsOeuvreInconnues: z.boolean(),

  // Back Text
  estampesMultiplesDescriptionInscriptionsDos: multilingualFieldSchema(
    z.string(),
  ),

  // Signature
  estampesMultiplesDescriptionSignatureLocalisation: multilingualFieldSchema(
    z.string(),
  ),
  estampesMultiplesDescriptionSignatureTranscription: z.string(),

  // Creation Date
  estampesMultiplesDescriptionDateDeCreation: z.string(),
  estampesMultiplesDescriptionDateDeCreationInconnue: z.boolean(),
  estampesMultiplesDescriptionDateDeCreationIncertaine: z.boolean(),
  estampesMultiplesDescriptionDateDeCreationEstimee: z.boolean(),

  // Registration Number
  estampesMultiplesDescriptionNumeroEnregistrement: z.string(),

  // Authors
  relatedAuthors: z.array(authorCoreInfoSchema),

  // Face media (image / vidéo / embed)
  url: z.string(),
  urlLarge: z.string().optional(),
  kind: z.string().optional(),
  videoPublicUrl: z.string().optional().nullable(),
  posterPublicUrl: z.string().optional().nullable(),
  embedProvider: z.string().optional(),
  embedVideoId: z.string().optional(),
  embedUrl: z.string().optional(),
  videoLoop: z.boolean().optional(),
});

export type MultipleprintCoreInfo = z.infer<typeof multipleprintCoreInfoSchema>;

export const tabMenuItems = [
  "artwork",
  "multipleprint",
  "exhibition",
  "bibliography",
  "auction",
  "author",
  "collector",
  "audiovisual",
  "document",
  "writing",
  "photography",
] as const;

export type TabMenuItems = (typeof tabMenuItems)[number];

// ─── Sculpture Edition ────────────────────────────────────────────────────────

export interface SculptureEdition {
  id: string;
  instanceId: string;
  masterArtworkId?: string | null;
  sculptureEditionPublicationStatus: PublicationStatus;
  sculptureEditionStatus?: string;

  // Identifiers
  sculptureEditionNumeroEnregistrement: string;
  sculptureEditionNumeroInventaire: string;
  sculptureEditionAutreNumero: string;
  sculptureEditionAncienNumero: string;

  // Edition metadata
  sculptureEditionTypeEdition: string;
  sculptureEditionDescriptionSocle: string;
  sculptureEditionDateRealisation: string;
  sculptureEditionFondeurFabricant: string;
  sculptureEditionNumeroFonte: string;

  // Multilingual descriptive fields
  sculptureEditionTechnique: LocaleDict<string>;
  sculptureEditionMateriau: LocaleDict<string>;
  sculptureEditionSupport: LocaleDict<string>;
  sculptureEditionNumerotation: LocaleDict<string>;
  sculptureEditionSignature: LocaleDict<string>;
  sculptureEditionMarqueTampons: LocaleDict<string>;
  sculptureEditionMarqueTamponsPublished: boolean;

  // Condition report
  sculptureEditionRapportCondition: string;
  sculptureEditionRapportConditionAuteur: string;
  sculptureEditionRapportConditionDate: string;
  sculptureEditionRapportConditionRecuArchive: boolean;

  // Certificat
  sculptureEditionCertificatAuteur: string;
  sculptureEditionCertificatLocalisationOriginal: string;
  sculptureEditionCertificatCopieExistante: boolean;
  sculptureEditionCertificatLocalisationCopie: string;
  sculptureEditionCertificatDate: string;
  sculptureEditionCertificatNotes: string;

  // Financial — Estimation
  sculptureEditionEstimationDescription: string;
  sculptureEditionEstimationMontant: MoneyAmount;

  // Financial — Assurance
  sculptureEditionAssuranceDescription: string;
  sculptureEditionAssuranceMontant: MoneyAmount;

  // Financial — Vente
  sculptureEditionVenteDescription: string;
  sculptureEditionVenteMontant: MoneyAmount;

  // Financial — Acquisition
  sculptureEditionAcquisitionDescription: string;
  sculptureEditionAcquisitionMontant: MoneyAmount;

  // Notes & Locations
  sculptureEditionNotes: string;
  sculptureEditionNotesTechniques: string;
  sculptureEditionRemarquesPubliquesMultilingual?: Record<
    SupportedLocale,
    string
  >;
  sculptureEditionRemarquesPubliquesPublished?: boolean;
  sculptureEditionLocation1: string;
  sculptureEditionLocation2: string;
  sculptureEditionLocation3: string;

  // Dimensions
  sculptureEditionDimensions: string;
  sculptureEditionPoids: string;
  sculptureEditionDimensionsSocle: string;
  sculptureEditionPoidsSocle: string;
  sculptureEditionDimensionsBrutes: string;
  sculptureEditionPoidsBrutes: string;
  sculptureEditionDimensionsAvecSocle: string;
  sculptureEditionPoidsSoclePiedouche: string;
  sculptureEditionDimensionsPersonnalisees: string;
  sculptureEditionPoidsLibre: string;
  sculptureEditionNotesPoids: string;
  createdAt: Date;
  updatedAt: Date | null;
  updatedById?: string;
  createdById?: string;
  updatedBy?: unknown;
  createdBy?: unknown;
  relatedArtwork: Artwork[];
  relatedMultipleprint: Multipleprint[];
  relatedAudiovisual: Audiovisual[];
  relatedDocument: Document[];
  relatedWriting: Writing[];
  relatedPhotography: Photography[];
  sculptureEditionInExhibitions: SculptureEditionInExhibition[];
  sculptureEditionInBibliography: SculptureEditionInBibliography[];
  sculptureEditionInAuction: SculptureEditionInAuction[];
  externalLinks?: ExternalLink[];
  attachments?: Attachment[];
  mouvements?: SculptureEditionMouvement[];
  history?: SculptureEditionHistoryEntry[];

  // Current collection + localisation
  collectorId?: string | null;
  collector?: Collector | null;
  nonLocalise?: boolean;
}

export interface SculptureEditionHistoryEntry {
  id: string;
  instanceId: string;
  sculptureEditionId: string;
  description: string;
  villePays: string;
  dates: string;
  collectionParticuliere: boolean;
  sequence: number;
  provenanceRank?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface SculptureEditionMouvement {
  id: string;
  instanceId: string;
  sculptureEditionId: string;
  description: string;
  lieu: string;
  dateDepart: string;
  dateRetour: string;
  constatEtatDepart: boolean;
  constatEtatRetour: boolean;
  notes: string;
  sequence: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface ExternalLink {
  id: string;
  name: string;
  url: string;
  dateConsultation: string;
  notes: string;
  relatedSculptureEditions?: SculptureEdition[];
}

export interface SculptureEditionInExhibition {
  id: string;
  instanceId?: string;
  sculptureEdition: SculptureEdition;
  /** Présent côté API jointe ; sinon dérivable de `sculptureEdition.id`. */
  sculptureEditionId?: string;
  exhibition: Exhibition;
  /** Présent côté API jointe ; sinon dérivable de `exhibition.id`. */
  exhibitionId?: string;
  sculptureEditionDansExpositionsNumeroCatalogue: string;
  sculptureEditionDansExpositionsNotes: string;
  itinerantGroupId?: string | null;
  itinerantOrder?: number | null;
  itinerantNote?: string;
  createdAt?: Date;
  updatedAt?: Date | null;
}

export interface SculptureEditionInBibliography {
  id: string;
  instanceId?: string;
  sculptureEdition: SculptureEdition;
  sculptureEditionId?: string;
  bibliography: Bibliography;
  bibliographyId?: string;
  sculptureEditionDansBibliographieReproduitPage: string;
  sculptureEditionDansBibliographieReproduitPlanche: string;
  sculptureEditionDansBibliographiePlancheNonNumerotee: boolean;
  sculptureEditionDansBibliographieCitePage: string;
  sculptureEditionDansBibliographieListePage: string;
  sculptureEditionDansBibliographieSousNumero: string;
  sculptureEditionDansBibliographiePhotographe: string;
  sculptureEditionDansBibliographieNonPagine: boolean;
  sculptureEditionDansBibliographieReproductionCouverture: boolean;
  sculptureEditionDansBibliographieReproductionCouleur: boolean;
  sculptureEditionDansBibliographieTexteLibre: string;
  sculptureEditionDansBibliographieNotes: string;
  createdAt?: Date;
  updatedAt?: Date | null;
}

export interface SculptureEditionInAuction {
  id: string;
  instanceId?: string;
  sculptureEdition: SculptureEdition;
  sculptureEditionId?: string;
  auction: Auction;
  auctionId?: string;
  provenanceRank?: number;
  sculptureEditionDansVentesNumeroLot: string;
  sculptureEditionDansVentesReproduitPage: string;
  sculptureEditionDansVentesEstimationHaute: string;
  sculptureEditionDansVentesEstimationBasse: string;
  sculptureEditionDansVentesVendu: boolean;
  sculptureEditionDansVentesResultatNonListe: boolean;
  sculptureEditionDansVentesNonVenu: boolean;
  sculptureEditionDansVentesNonVendu: boolean;
  sculptureEditionDansVentesNonReproduit: boolean;
  sculptureEditionDansVentesRetire: boolean;
  sculptureEditionDansVentesMiseAPrix: string;
  sculptureEditionDansVentesPrixMarteau: string;
  sculptureEditionDansVentesPrixAvecFrais: string;
  sculptureEditionDansVentesNotes: string;
  createdAt?: Date;
  updatedAt?: Date | null;
}
